import { ReactComponent as BorderStyleIcon } from 'assets/icons/border_style.svg';
import { ReactComponent as LineWidthIcon } from 'assets/icons/line_width.svg';
import { ColorPicker } from 'modules/Editor/components/ColorPicker';
import { DropdownPopover, SliderPopover } from 'modules/common/components/ToolbarPopover';
import { generateTranslatedOptions } from 'modules/common/components/ToolbarPopover/DropdownPopover';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LineWidgetTypes } from 'widgets/Lines/LineWidget/LineWidget.types';

import { ArrowStyleTypes, LineWidgetDashArrayTypes } from '../CommonLine.types';
import {
  ARROW_OPTIONS,
  LINE_MAX_WIDTH,
  LINE_MIN_WIDTH,
  LINE_STYLE_OPTIONS,
  LINE_TYPE_OPTIONS,
} from './CommonLineToolbar.config';
import { generateArrowStyleOptions } from './CommonLineToolbar.helpers';

type LineWidgetToolbarValues = {
  strokeWidth: number;
  strokeColor: string | string[];
  strokeDashType: string;
  startArrowStyle: string;
  endArrowStyle: string;
  type?: string;
};

export enum LineToolbarOptions {
  lineType = 'lineType',
  startArrow = 'startArrow',
  endArrow = 'endArrow',
}

interface LineWidgetToolbarContentProps {
  values: LineWidgetToolbarValues;
  onUpdate: (
    updateData: Partial<{
      strokeWidth: number;
      strokeColor: string;
      strokeDashType: LineWidgetDashArrayTypes;
      startArrowStyle: ArrowStyleTypes;
      endArrowStyle: ArrowStyleTypes;
      type: LineWidgetTypes;
    }>,
  ) => void;
  disabledOptions?: LineToolbarOptions[];
  hiddenOptions?: LineToolbarOptions[];
}

export const LineWidgetToolbarContent = ({
  values,
  onUpdate,
  disabledOptions,
  hiddenOptions,
}: LineWidgetToolbarContentProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'lineToolbar' });
  const { type, strokeColor, strokeWidth, strokeDashType, startArrowStyle, endArrowStyle } = values;

  const handleChangeStrokeWidth = (newValue: number) => onUpdate({ strokeWidth: newValue });

  const setStrokeColor = (color: string) => onUpdate({ strokeColor: color });

  const handleUpdateLineStyle = (style: LineWidgetDashArrayTypes) =>
    onUpdate({ strokeDashType: style || LineWidgetDashArrayTypes.solid });
  const selectedLineStyleIndex = LINE_STYLE_OPTIONS.findIndex((option) => option.value === strokeDashType);

  const handleUpdateLineType = (newType: LineWidgetTypes) => onUpdate({ type: newType || LineWidgetTypes.straight });
  const selectedLineTypeIndex = LINE_TYPE_OPTIONS.findIndex((option) => option.value === type);

  const handleUpdateStartArrow = (startArrow: ArrowStyleTypes) =>
    onUpdate({ startArrowStyle: startArrow || ArrowStyleTypes.basic });
  const selectedStartArrowIndex = ARROW_OPTIONS.findIndex((option) => option.value === startArrowStyle);

  const handleUpdateEndArrow = (endArrow: ArrowStyleTypes) =>
    onUpdate({ endArrowStyle: endArrow || ArrowStyleTypes.basic });
  const selectedEndArrowIndex = ARROW_OPTIONS.findIndex((option) => option.value === endArrowStyle);

  const startArrowOptions = useMemo(() => generateArrowStyleOptions(true), []);
  const endArrowOptions = useMemo(() => generateArrowStyleOptions(false), []);

  const lineWeightLabel = t('lineWeight.label');
  const startArrowLabel = t('arrow.start');
  const endArrowLabel = t('arrow.end');
  const lineTypeLabel = t('lineType.label');
  const lineStyleLabel = t('lineStyle.label');
  const lineColorLabel = t('lineColor.label');

  const isMultiStrokeColor = Array.isArray(strokeColor);
  return (
    <>
      <ColorPicker
        color={isMultiStrokeColor ? strokeColor[0] : strokeColor}
        iconColors={isMultiStrokeColor ? strokeColor : undefined}
        onChange={setStrokeColor}
        label={lineColorLabel}
        showNoColorOption={false}
        showNoColorChecker={isMultiStrokeColor}
      />

      <SliderPopover
        value={strokeWidth}
        icon={<LineWidthIcon />}
        label={lineWeightLabel}
        onChange={handleChangeStrokeWidth}
        suffix={'px'}
        max={LINE_MAX_WIDTH}
        min={LINE_MIN_WIDTH}
        title={lineWeightLabel}
      />

      <DropdownPopover
        selectedIndex={selectedLineStyleIndex > -1 ? selectedLineStyleIndex : 0}
        options={generateTranslatedOptions(t, LINE_STYLE_OPTIONS)}
        toolbarIcon={<BorderStyleIcon />}
        label={lineStyleLabel}
        onSelect={handleUpdateLineStyle}
        w={'11.5rem'}
        justifyContent={'flex-start'}
        closeOnSelect
        returnFocusOnClose={false}
        useLabelInsteadOfSelectedLabel
      />

      {!hiddenOptions?.includes(LineToolbarOptions.lineType) && (
        <DropdownPopover
          selectedIndex={selectedLineTypeIndex}
          options={generateTranslatedOptions(t, LINE_TYPE_OPTIONS)}
          toolbarIcon={LINE_TYPE_OPTIONS[selectedLineTypeIndex].icon}
          label={lineTypeLabel}
          onSelect={handleUpdateLineType}
          w={'9.2rem'}
          justifyContent={'flex-start'}
          closeOnSelect
          returnFocusOnClose={false}
          triggerProps={{ isDisabled: disabledOptions?.includes(LineToolbarOptions.lineType) }}
        />
      )}

      <DropdownPopover
        selectedIndex={selectedStartArrowIndex}
        options={generateTranslatedOptions(t, startArrowOptions)}
        toolbarIcon={startArrowOptions[selectedStartArrowIndex].buttonIcon}
        label={startArrowLabel}
        onSelect={handleUpdateStartArrow}
        w={'11rem'}
        justifyContent={'flex-start'}
        closeOnSelect
        returnFocusOnClose={false}
        triggerProps={{ isDisabled: disabledOptions?.includes(LineToolbarOptions.startArrow) }}
        useLabelInsteadOfSelectedLabel
      />

      <DropdownPopover
        selectedIndex={selectedEndArrowIndex}
        options={generateTranslatedOptions(t, endArrowOptions)}
        toolbarIcon={endArrowOptions[selectedEndArrowIndex].buttonIcon}
        label={endArrowLabel}
        onSelect={handleUpdateEndArrow}
        w={'11rem'}
        justifyContent={'flex-start'}
        closeOnSelect
        returnFocusOnClose={false}
        triggerProps={{ isDisabled: disabledOptions?.includes(LineToolbarOptions.endArrow) }}
        useLabelInsteadOfSelectedLabel
      />
    </>
  );
};
